import formStyles from 'styles/forms.scss';
import DateTimePicker from 'common/components/DateTime/DateTimePicker';
import { mutateInputWithCurrentTime } from 'common/util/dateTime';

/**
 * Mutates the current date time input,
 * and returns a date time string to the date picker value.
 *
 * @param {{ input: * }} options 
 */
export const DateTimePickerMutateDate = ({ input }) => {
    return (
        <DateTimePicker
            value={input.value}
            onChange={date => {
                const mutatedDate = mutateInputWithCurrentTime(date, 'DATEONLY');
                return input.onChange(mutatedDate.toISOString());
            }}
            time={false} 
        />
    );
};

/**
 * Removes the time from date,
 * and returns a date only string to the date picker value.
 * 
 * Useful for when we want the date value to be just a date, with no time.
 *
 * This is done this way so it won't break current/old code that is using the other way.
 *
 * @param {{ input: * }} options 
 */
export const DateTimePickerStr = ({ input }) => {
    return (
        <DateTimePicker
            noTimezone={true}
            value={input.value}
            onChange={(_, dateStr) => input.onChange(dateStr)}
            time={false} 
        />
    );
};

export const renderSingleDatePicker = ({ name, label, input, className, required, noTimezone = false, dateWrapperClassName = null }) => {
    const elementStyle = className || formStyles.formElement;
    const requiredClass = required ? formStyles.required : '';
    const dateWrapperStyle = dateWrapperClassName || formStyles.dateWrapper;
    return (
        <div className={elementStyle} data-cy="Form-renderSingleDatePicker">
            <label className={requiredClass}>{label}</label>
            <div className={dateWrapperStyle}>
                { noTimezone ? <DateTimePickerStr input={input} /> : <DateTimePickerMutateDate input={input}/> }
            </div>
        </div>
    );
};

export const renderInputField = ({name, input, label, type, disabled, className, placeholder, required, meta: { touched, error }, autoFocus = false }) => {
    const inputStyle = touched && error ? formStyles.inputTextBoxError : formStyles.inputTextBox;
    const elementStyle = className || formStyles.formElement;
    const requiredClass = required ? formStyles.required : '';
    return (
        <div className={elementStyle} data-cy="Form-renderInputField">
            <label className={requiredClass} htmlFor={name} data-cy="Form-label">
                {label}
            </label>
            <div className={formStyles.formInput}>
                <input {...input}
                    disabled={disabled || false}
                    id={name}
                    type={type}
                    placeholder={placeholder || label}
                    autoComplete="off"
                    className={inputStyle}
                    autoFocus={autoFocus}
                    data-cy={'Form-renderInputField-' + name}
                />
                { touched && error && <span className={formStyles.formError} data-cy="Form-errorer">{error}</span> }
            </div>
        </div>
    );
};

export const renderReadOnlyInputField = ({ name, input, label, type, required }) => {
    return (
        <div className={formStyles.formElement} data-cy="Form-renderReadOnlyInputField">
            <label htmlFor={name} data-cy="Form-name">
                {label}
            </label>
            <div className={formStyles.formInput}>
                <input {...input}
                    disabled={true}
                    id={name}
                    type={type}
                    placeholder={label}
                    autoComplete="off"
                    className={formStyles.disabledFormInput}
                    data-cy={'Form-input-' + name}
                />
            </div>
        </div>
    );
};

export const renderTextareaField = ({name, input, label, type, disabled, required, meta: { touched, error }}) => {
    const inputStyle = touched && error ? formStyles.inputTextBoxError : formStyles.inputTextBox;
    const requiredClass = required ? formStyles.required : '';
    return (
        <div className={formStyles.formElement} data-cy="Form-renderTextareaField">
            <label className={requiredClass} htmlFor={name} data-cy="Form-renderlabel">
                {label}
            </label>
            <div className={formStyles.formInput}>
                <textarea {...input}
                    disabled={disabled || false}
                    id={name}
                    type={type}
                    placeholder={label}
                    autoComplete="off"
                    className={inputStyle}
                    data-cy={'Form-textarea-' + name}
                />
                { touched && error && <span className={formStyles.formError} data-cy="Form-error">{error}</span> }
            </div>
        </div>
    );
};

export const renderSelectField = ({name, input, label, options, multiple, disabled, required, meta: { touched, error }}) => {
    const selectStyle = touched && error ? formStyles.dropdownListError : formStyles.dropdownList;
    const optionElements = Array.isArray(options)
        ? options.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)
        : null;
    const isMultiSelect = multiple === true;
    const requiredClass = required ? formStyles.required : '';
    return (
        <div className={formStyles.formElement} data-cy="Form-renderSelectField">
            <label className={requiredClass} htmlFor={name}  data-cy="Form-LabelField">
                { label }
            </label>
            <div className={formStyles.formInput}>
                <select {...input} multiple={isMultiSelect} id={name} className={selectStyle} disabled={typeof disabled !== 'undefined' && disabled} data-cy="Form-select">
                    <option value="" disabled>{label}</option>
                    { optionElements }
                </select>
                { touched && error && <span className={formStyles.formError} data-cy="Form-formError">{error}</span> }
            </div>
        </div>
    );
};
